import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import { Loader } from 'rsuite';

import { useMessenger } from '../../context/messengerContext';
import { getNotificationsList } from '../../api/notifications';
import { formatDateTime } from '../../utils/format';

import './notificationsList.css';
import { NotificationsFooter } from './NotificationsFooter';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';

const PAGE_LIMIT = 50;

export default function NotificationsList() {
    const { t } = useTranslation('basic');
    const { userId, platform, showBackButton, hideBackButton } = useMessenger();
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 0, pages: 0 });
    const [error, setError] = useState(null);

    const isIOS26Version = isIOSVersionHigherThan26Version();

    const loadPage = useCallback(
        async (page) => {
            setLoading(true);
            try {
                const { items: newItems, pagination: nextPagination } =
                    await getNotificationsList({
                        platform,
                        userId,
                        page,
                        limit: PAGE_LIMIT,
                    });
                if (page === 1) {
                    setItems(newItems);
                } else {
                    setItems((prev) => [...prev, ...newItems]);
                }
                setPagination(nextPagination);
                setError(null);
            } catch (err) {
                if (page === 1) {
                    setError(err?.message || t('error_loading_tariffs'));
                }
            } finally {
                setLoading(false);
                if (page === 1) setInitialLoading(false);
            }
        },
        [platform, userId, t],
    );

    const loadMore = useCallback(() => {
        if (loading || pagination.page >= pagination.pages) return;
        loadPage(pagination.page + 1);
    }, [loading, pagination.page, pagination.pages, loadPage]);

    useEffect(() => {
        showBackButton(() => navigate(-1));
        return () => hideBackButton();
    }, [showBackButton, hideBackButton, navigate]);

    useEffect(() => {
        loadPage(1);
    }, [userId]);

    const header = (
        <div className={`stepsHeaderWrapper gradient_bg_header ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
            <div className='stepHeaderHeader heading-h4' style={{ paddingBottom: '3.45rem' }}>
                {t('notifications_title')}
                <img
                    id='stepsHeaderImage'
                    src='https://s3.timeweb.com/8a6e091b-publick/template_header.png'
                    alt=''
                />
            </div>
        </div>
    );

    if (initialLoading) {
        return (
            <>
                {header}
                <div className='notificationsList'>
                    <div className='notificationsList_loading'>
                        <div className='loader-container'>
                            <Loader size='lg' />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                {header}
                <div className='notificationsList'>
                    <p className='notificationsList_error'>{error}</p>
                </div>
            </>
        );
    }

    if (items.length === 0) {
        return (
            <>
                {header}
                <div className='notificationsList'>
                    <p className='notificationsList_empty'>
                        {t('notifications_empty')}
                    </p>
                </div>
            </>
        );
    }

    const context = { loading, loadMore, pagination };

    return (
        <>
            {header}
            <div className='notificationsList'>
                <div className='notificationsList_virtuosoWrap'>
                    <Virtuoso
                        style={{ height: '100%' }}
                        data={items}
                        context={context}
                        increaseViewportBy={200}
                        itemContent={(index, item) => (
                            <div
                                role='button'
                                tabIndex={0}
                                className='notificationsList_item'
                                onClick={() =>
                                    navigate(`/notification/${item.id}`)
                                }
                                onKeyDown={(e) => {
                                    if (
                                        e.key === 'Enter' ||
                                        e.key === ' '
                                    ) {
                                        e.preventDefault();
                                        navigate(
                                            `/notification/${item.id}`,
                                        );
                                    }
                                }}
                            >
                                <div className='notificationsList_itemTitle'>
                                    {item.title ?? '—'}
                                </div>
                                <div className='notificationsList_itemText'>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: item.text ?? '—',
                                        }}
                                    ></div>
                                </div>
                                <div className='notificationsList_itemDate'>
                                    {formatDateTime(item.created_at)}
                                </div>
                            </div>
                        )}
                        components={{ Footer: NotificationsFooter }}
                    />
                </div>
            </div>
        </>
    );
}
